import { Type, ToggleRight, Copy, Settings, Palette, X, Sparkles, Eye } from 'lucide-react';

interface SubtitleGuideModalProps {
  open: boolean;
  onClose: () => void;
}

export function SubtitleGuideModal({ open, onClose }: SubtitleGuideModalProps) {
  if (!open) return null;

  const steps = [
    {
      icon: ToggleRight,
      title: 'テロップをオンにする',
      description: 'シーン一覧の上にあるスイッチをオンにすると、動画にテロップ（字幕）が表示されるようになります。',
      extra: (
        <div className="mt-2 text-xs text-warm-500 space-y-2">
          <div className="space-y-1">
            <p className="flex items-center gap-1">
              <Type size={12} className="text-coral-500" />
              <span className="font-medium text-warm-600">全体スイッチ:</span> すべてのシーンのテロップをまとめてオン/オフ
            </p>
            <p className="flex items-center gap-1">
              <Eye size={12} className="text-coral-500" />
              <span className="font-medium text-warm-600">シーンごと:</span> 特定のシーンだけオン/オフしたい場合に使用
            </p>
          </div>
          <p className="text-amber-600 bg-amber-50 px-2 py-1 rounded">
            ※ 全体スイッチがオフだと、シーンごとの設定に関係なくテロップは表示されません
          </p>
        </div>
      ),
    },
    {
      icon: Copy,
      title: 'テロップに表示する文字を入力する',
      description: '各シーンの「テロップ」欄に、動画に表示したい文字を入力します。読み上げと同じ内容でよければ、コピーボタンを押すだけで入力できます。',
      extra: (
        <ul className="mt-2 text-xs text-warm-500 space-y-1">
          <li><span className="font-medium text-warm-600">1つずつコピー:</span> 各シーンにあるコピーボタン</li>
          <li><span className="font-medium text-warm-600">まとめてコピー:</span> 上部のコピーボタンで全シーン一括</li>
        </ul>
      ),
    },
    {
      icon: Settings,
      title: '見た目を設定する',
      description: '上部の歯車ボタンから、テロップの見た目（フォント、色、位置など）を設定できます。ここで設定した内容はすべてのシーンに反映されます。',
      extra: (
        <div className="mt-3 p-3 bg-warm-50 rounded-xl text-sm space-y-2">
          <p className="font-medium text-warm-700">設定できる項目:</p>
          <ul className="text-warm-600 space-y-1 text-xs">
            <li>フォントの種類 / 文字の大きさ / 太字</li>
            <li>文字の色 / 縁取り / 背景色</li>
            <li>画面のどの位置に表示するか</li>
          </ul>
        </div>
      ),
    },
    {
      icon: Palette,
      title: 'シーンごとに見た目を変える（お好みで）',
      description: '「このシーンだけ違う色にしたい」など、特定のシーンだけ見た目を変えたい場合は、各シーンのパレットボタンから設定できます。',
      extra: (
        <p className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded inline-block">
          シーンごとの設定は、全体設定より優先されます
        </p>
      ),
    },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="flex items-center gap-2">
            <Type size={20} className="text-coral-500" />
            テロップの使い方
          </h2>
          <button
            onClick={onClose}
            className="btn-icon"
            aria-label="閉じる"
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body space-y-5">
          {steps.map((step, index) => (
            <section key={index} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-xl bg-coral-100 flex items-center justify-center shrink-0">
                  <step.icon size={20} className="text-coral-600" />
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 flex-1 bg-coral-100 mt-2" />
                )}
              </div>
              <div className="flex-1 pb-4">
                <h3 className="font-display font-semibold text-warm-800 mb-1">
                  {index + 1}. {step.title}
                </h3>
                <p className="text-sm text-warm-600 leading-relaxed">
                  {step.description}
                </p>
                {step.extra}
              </div>
            </section>
          ))}

          {/* Tips */}
          <section className="bg-gradient-to-br from-coral-50 to-coral-100 p-4 rounded-xl border border-coral-200">
            <h3 className="font-display font-semibold text-coral-800 mb-2 flex items-center gap-2">
              <Sparkles size={16} />
              知っておくと便利
            </h3>
            <ul className="text-sm text-coral-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-coral-400 mt-2 shrink-0" />
                設定画面のプレビューは実際の動画と同じ見た目で確認できます
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-coral-400 mt-2 shrink-0" />
                テロップは最大2行まで表示されます（長い文章は自動で改行）
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-coral-400 mt-2 shrink-0" />
                日本語の文章は読みやすい位置で自動的に改行されます
              </li>
            </ul>
          </section>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-primary w-full">
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
